import { Component, type ErrorInfo, type ReactNode } from 'react'
import { trackEvent } from '../../lib/analytics'
import { isChunkLoadError, canAutoReload, attemptChunkReload } from '../../lib/chunkReload'

interface Props {
  children: ReactNode
  fallback: ReactNode
  // A quiet placeholder rendered while an automatic reload is in flight,
  // so the error fallback does not flash for the split second before the
  // browser navigates.
  reloading: ReactNode
  // While the fallback is showing, a change of resetKey clears the error
  // state so the children get a fresh render (route recovery: navigating
  // away from a tripped page via Header/MobileNav). Deliberately NOT a
  // React key on the boundary: a key would remount the healthy subtree on
  // every navigation and wipe all page state. Inert while no error shows.
  resetKey?: unknown
}

interface State {
  hasError: boolean
  reloading: boolean
}

// Last-resort boundary around the routed page tree. Its main job is the
// stale-chunk case: a deploy replaced the hashed /assets chunks under this
// tab, a lazy route import rejected, and (pre-boundary) React 19 unmounted
// the whole tree to a blank page. Chunk errors that reach here trigger ONE
// automatic reload per 60s window (see chunkReload.ts); everything else,
// and a chunk error inside the guard window, renders the fallback.
//
// getDerivedStateFromError stays pure (StrictMode double-invokes render
// phase); the sessionStorage write + reload happen in componentDidCatch.
export default class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, reloading: false }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      reloading: isChunkLoadError(error) && canAutoReload(),
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    trackEvent('error', { message: error.message?.slice(0, 100) || 'unknown' })
    // eslint-disable-next-line no-console
    console.error('RouteErrorBoundary caught:', error, info)
    if (isChunkLoadError(error) && !attemptChunkReload() && this.state.reloading) {
      // Guard lost a race (another boundary instance reloaded first, or
      // storage failed): show the manual fallback instead of a dead spinner.
      this.setState({ reloading: false })
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, reloading: false })
    }
  }

  render() {
    if (this.state.reloading) return this.props.reloading
    if (this.state.hasError) return this.props.fallback
    return this.props.children
  }
}
